import { useCallback, useEffect, useRef, useState } from 'react';

export const DEFAULT_CHAR_INTERVAL_MS = 28;

export interface UseTypewriterOptions {
  text: string | null | undefined;
  charIntervalMs?: number;
  reduced?: boolean;
}

export interface TypewriterState {
  displayed: string;
  isDone: boolean;
  revealAll: () => void;
}

/**
 * Reveals characters of `text` at a steady cadence. If `reduced` is set, jumps
 * to the full string on arrival — visual state still lands, just instantly.
 * Clears itself when text becomes null/empty.
 */
export function useTypewriter({
  text,
  charIntervalMs = DEFAULT_CHAR_INTERVAL_MS,
  reduced = false,
}: UseTypewriterOptions): TypewriterState {
  const [displayed, setDisplayed] = useState('');
  const intervalRef = useRef<number | null>(null);

  const clearTyping = useCallback(() => {
    if (intervalRef.current === null) return;
    window.clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  useEffect(() => {
    clearTyping();
    if (!text) {
      setDisplayed('');
      return undefined;
    }
    if (reduced) {
      setDisplayed(text);
      return undefined;
    }
    setDisplayed('');
    let cursor = 0;
    intervalRef.current = window.setInterval(() => {
      cursor += 1;
      setDisplayed(text.slice(0, cursor));
      if (cursor >= text.length) clearTyping();
    }, charIntervalMs);
    return () => {
      clearTyping();
    };
  }, [text, charIntervalMs, clearTyping, reduced]);

  const revealAll = useCallback(() => {
    if (!text) return;
    clearTyping();
    setDisplayed(text);
  }, [clearTyping, text]);

  const isDone = typeof text === 'string' && text.length > 0 && displayed === text;
  return { displayed, isDone, revealAll };
}
